import { useState, useEffect, useMemo } from 'react'
import { HTTP_PROXY_BASE } from '../utils/chartUtils'

/**
 * CalendarPanel - this week's scheduled economic releases (Forex Factory
 * feed, cached hourly by the server at /econ/calendar). Fetches on open and
 * every 30 min. USD by default, ALL toggle. Local time, grouped by day.
 */

const REFRESH_MS = 30 * 60 * 1000
const IMPACT_COLOR = { High: 'var(--down)', Medium: '#ff9a1f', Low: 'var(--text-lo)', Holiday: 'var(--text-lo)' }
const CSS = `
.cal-panel{display:flex;flex-direction:column;height:100%;width:100%;background:var(--surface);color:var(--text-hi);font-size:11px;font-variant-numeric:tabular-nums;min-height:0}
.cal-head{display:flex;align-items:center;gap:6px;height:var(--rail-h);padding:0 8px;background:var(--surface-rail);border-bottom:1px solid var(--seam);flex-shrink:0}
.cal-title{color:var(--text-mid);letter-spacing:.08em;font-weight:600;margin-right:auto}
.cal-btn{background:none;border:1px solid var(--seam);color:var(--text-mid);font:inherit;font-size:10px;padding:2px 7px;cursor:pointer}
.cal-btn.on{color:var(--accent);border-color:var(--accent)}
.cal-body{flex:1;overflow-y:auto;min-height:0;scrollbar-color:#000 #000;scrollbar-width:thin}.cal-body::-webkit-scrollbar{width:6px;background:#000}.cal-body::-webkit-scrollbar-thumb{background:#000}.cal-body::-webkit-scrollbar-track{background:#000}.cal-body::-webkit-scrollbar-corner{background:#000}
.cal-day{position:sticky;top:0;background:var(--surface-rail);color:var(--text-mid);padding:3px 8px;font-size:10px;letter-spacing:.06em;border-bottom:1px solid var(--seam)}
.cal-day.today{color:var(--accent)}
.cal-row{display:grid;grid-template-columns:44px 10px minmax(0,1fr) 50px 50px 50px;align-items:center;gap:4px;padding:3px 8px;border-bottom:1px solid var(--seam)}
.cal-row.past{opacity:.38}
.cal-row.hi .cal-t{color:var(--text-hi);font-weight:600}
.cal-row.next{background:var(--surface-active);box-shadow:inset 2px 0 0 var(--accent)}
.cal-time{color:var(--text-mid)}
.cal-dot{width:7px;height:7px;border-radius:50%}
.cal-t{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:var(--text-mid)}
.cal-cc{color:var(--text-lo);margin-right:4px}
.cal-num{text-align:right;color:var(--text-mid);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.cal-cols{color:var(--text-lo);font-size:9px;letter-spacing:.06em;padding:2px 8px}
.cal-msg{padding:12px 8px;color:var(--text-lo)}
.cal-act{text-align:right;color:var(--text-hi);overflow:hidden;white-space:nowrap}
.cal-act.beat{color:var(--up)}
.cal-act.miss{color:var(--down)}
.cal-g{color:var(--text-lo);font-size:8px;margin-left:1px;vertical-align:super}
@media (max-width:480px){.cal-row{grid-template-columns:38px 8px minmax(0,1fr) 42px 42px 42px;gap:3px;padding:3px 6px}}
.cal-next-tag{color:var(--accent);font-size:9px;margin-left:4px}
`

// Lower-is-better releases: a print under forecast is a beat.
const INVERT = /unemployment|claims|jobless|inventories/i
const numOf = (v) => {
  const m = String(v || '').replace(/,/g, '').match(/^\s*(-?\d+(?:\.\d+)?)\s*([%KMBT]?)/i)
  if (!m) return null
  const mul = { K: 1e3, M: 1e6, B: 1e9, T: 1e12 }[m[2].toUpperCase()] || 1
  return parseFloat(m[1]) * mul
}
const actClass = (e) => {
  const a = numOf(e.actual), f = numOf(e.forecast)
  if (a === null || f === null || a === f) return ''
  return ((a > f) !== INVERT.test(e.title || '')) ? ' beat' : ' miss'
}

const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`

export default function CalendarPanel() {
  const [events, setEvents] = useState(null)
  const [err, setErr] = useState(null)
  const [all, setAll] = useState(() => localStorage.getItem('cheddar_cal_all') === '1')
  const [now, setNow] = useState(Date.now())

  useEffect(() => {
    let dead = false
    const load = async () => {
      try {
        const r = await fetch(`${HTTP_PROXY_BASE}/econ/calendar`)
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const j = await r.json()
        if (!dead) { setEvents(Array.isArray(j.events) ? j.events : []); setErr(null) }
      } catch (e) { if (!dead) setErr(e.message || 'failed') }
    }
    load()
    const id = setInterval(load, REFRESH_MS)
    const tick = setInterval(() => setNow(Date.now()), 60 * 1000)
    return () => { dead = true; clearInterval(id); clearInterval(tick) }
  }, [])

  useEffect(() => { localStorage.setItem('cheddar_cal_all', all ? '1' : '0') }, [all])

  const { days, nextIdx } = useMemo(() => {
    const list = (events || [])
      .filter(e => all || e.country === 'USD')
      .map(e => ({ ...e, ts: Date.parse(e.date) }))
      .filter(e => Number.isFinite(e.ts))
      .sort((a, b) => a.ts - b.ts)
    const n = list.findIndex(e => e.ts >= now)
    const out = []
    list.forEach((e, i) => {
      const d = new Date(e.ts)
      const k = dayKey(d)
      if (!out.length || out[out.length - 1].k !== k) out.push({ k, d, rows: [] })
      out[out.length - 1].rows.push({ ...e, i })
    })
    return { days: out, nextIdx: n }
  }, [events, all, now])

  const todayK = dayKey(new Date(now))
  const fmtTime = (ts, title) => {
    const d = new Date(ts)
    if (d.getHours() === 0 && d.getMinutes() === 0 && /day|holiday/i.test(title || '')) return 'All day'
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, m => m.trim()[0].toLowerCase())
  }

  return (
    <div className="cal-panel">
      <style>{CSS}</style>
      <div className="cal-head">
        <span className="cal-title">CALENDAR</span>
        <button className={`cal-btn${!all ? ' on' : ''}`} onClick={() => setAll(false)}>USD</button>
        <button className={`cal-btn${all ? ' on' : ''}`} onClick={() => setAll(true)}>ALL</button>
      </div>
      <div className="cal-cols cal-row" style={{ borderBottom: '1px solid var(--seam)' }}>
        <span>TIME</span><span /><span>EVENT</span><span className="cal-num">ACT</span><span className="cal-num">FCST</span><span className="cal-num">PREV</span>
      </div>
      <div className="cal-body">
        {events === null && !err && <div className="cal-msg">Loading…</div>}
        {err && !events && <div className="cal-msg">Calendar unavailable ({err})</div>}
        {events && !days.length && <div className="cal-msg">No events this week.</div>}
        {days.map(g => (
          <div key={g.k}>
            <div className={`cal-day${g.k === todayK ? ' today' : ''}`}>
              {g.d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase()}
              {g.k === todayK ? ' · TODAY' : ''}
            </div>
            {g.rows.map(e => {
              const past = e.ts < now
              const hi = e.impact === 'High' && !past && (g.k === todayK || e.ts > now)
              const next = e.i === nextIdx
              return (
                <div key={e.i} className={`cal-row${past ? ' past' : ''}${hi ? ' hi' : ''}${next ? ' next' : ''}`}
                  title={`${e.country} · ${e.impact} impact`}>
                  <span className="cal-time">{fmtTime(e.ts, e.title)}</span>
                  <span className="cal-dot" style={{ background: IMPACT_COLOR[e.impact] || 'var(--text-lo)' }} />
                  <span className="cal-t">
                    {all && <span className="cal-cc">{e.country}</span>}
                    {e.title}
                    {next && <span className="cal-next-tag">NEXT</span>}
                  </span>
                  {e.actual
                    ? <span className={`cal-act${actClass(e)}`} title="via Grok">{e.actual}<span className="cal-g">G</span></span>
                    : <span className="cal-num">–</span>}
                  <span className="cal-num">{e.forecast || '–'}</span>
                  <span className="cal-num">{e.previous || '–'}</span>
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
